import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'

import { RouterProvider } from '@tanstack/react-router'

import { initClarity } from './integrations/clarity'
import { shouldInitGleap } from './integrations/gleap'
import { initGTM } from './integrations/gtm'
import { NuqsProvider } from './integrations/nuqs'
import { initPostHog, PostHogAppProvider } from './integrations/posthog'
import { initSentry } from './integrations/sentry'
import * as TanStackQueryProvider from './integrations/tanstack-query/root-provider.tsx'
import { captureAccessLinkFragment } from './lib/access-link-fragment'
import { analytics } from './lib/analytics'
import {
  ClarityProvider,
  GleapProvider,
  GTMDataLayerProvider,
  PostHogAnalyticsProvider,
} from './lib/analytics/providers'
import { logger } from './lib/logger'
import {
  ConsoleProvider,
  PostHogLoggerProvider,
  SentryProvider,
} from './lib/logger/providers'
import { subscribeToCrossTabAuth } from './lib/refresh-token'
import { isChunkLoadError } from './lib/version'
import reportWebVitals from './reportWebVitals.ts'
import { createAppRouter } from './router'

import './styles.css'

import './polyfills'
import './lib/prevent-native-drag'
import './lib/theme'

captureAccessLinkFragment()

function handlePreMountChunkError(error: unknown, source: string) {
  if (!isChunkLoadError(error)) return

  const appRoot = document.getElementById('app')
  const reactHasMounted = !!appRoot && appRoot.innerHTML.length > 0
  if (reactHasMounted) return

  const message = error instanceof Error ? error.message : String(error)
  logger.warning('Chunk load error (pre-mount)', { message, source }, 'router')

  const lastReload = sessionStorage.getItem('chunk-error-reload')
  const now = Date.now()
  if (!lastReload || now - parseInt(lastReload) > 10000) {
    sessionStorage.setItem('chunk-error-reload', now.toString())
    window.location.reload()
  }
}

window.addEventListener('error', (event) => {
  handlePreMountChunkError(event.error ?? new Error(event.message), 'error')
})

window.addEventListener('unhandledrejection', (event) => {
  handlePreMountChunkError(event.reason, 'unhandledrejection')
})

initSentry()
initClarity()
initPostHog()
initGTM()

// Gleap is initialized later by <GleapBootstrap /> inside RootLayout, once we
// know whether the tenant is white-label. We only register its analytics
// provider eagerly here so events can flow through `analytics.*`; the provider
// itself short-circuits via `isGleapReady()` until init actually runs.
const gleapAnalyticsEnabled = shouldInitGleap()

// Initialize analytics providers
analytics.registerProvider(new PostHogAnalyticsProvider())
analytics.registerProvider(new GTMDataLayerProvider())
analytics.registerProvider(new ClarityProvider())
if (gleapAnalyticsEnabled) {
  analytics.registerProvider(new GleapProvider())
}

// Initialize logger providers
logger.registerProvider(new SentryProvider())
logger.registerProvider(new PostHogLoggerProvider())

if (import.meta.env.DEV) {
  logger.registerProvider(new ConsoleProvider())
}

subscribeToCrossTabAuth()

const TanStackQueryProviderContext = TanStackQueryProvider.getContext()
const router = createAppRouter({
  ...TanStackQueryProviderContext,
})

const rootElement = document.getElementById('app')
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <PostHogAppProvider>
        <NuqsProvider>
          <TanStackQueryProvider.Provider {...TanStackQueryProviderContext}>
            <RouterProvider router={router} />
          </TanStackQueryProvider.Provider>
        </NuqsProvider>
      </PostHogAppProvider>
    </StrictMode>,
  )
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
